import React, { useEffect, useMemo, useState } from 'react'
import { Box, Stack, Text } from '@mantine/core'

import {
  BookedByReporterFromDifferentStateFilter,
  EventLevelFilter,
  GeneralStatusFilter,
  ReporterFilter,
  ReporterStatusFilter,
  SearchFilter,
  StateFilter,
} from '@/components/Calendar/FilterControls'
import { useReportersQuery } from '@/queries/reporters'
import { Event, ReporterBase, ReporterEvent } from '@/types'
import { LEVELS } from '@/utils/parrot'

export type Filters = {
  general_status_in: Event['general_status'][]
  reporter_status_in: ReporterEvent['reporter_status'][]
  reporter?: ReporterBase
  event_level_in?: number[]
  event_search?: string
  team_search?: string
  organization_search?: string
  state_in?: string[]
  booked_by_reporter_from_different_state?: boolean
}

export const FilterControls: React.FC<{
  values: Filters
  onChange: (filters: Filters) => void
}> = ({ values: initialFilters, onChange }) => {
  const [filters, setFilters] = useState<Filters>(initialFilters)
  const { data: allReporters = [] } = useReportersQuery()

  const reporters = useMemo(
    () => allReporters.filter(r => r.is_active),
    [allReporters],
  )

  const selectedReporter = reporters.find(
    reporter => reporter.id === filters.reporter?.id,
  )

  const maxLevel = selectedReporter?.level

  useEffect(() => {
    onChange(filters)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters])

  return (
    <Box ml="xl" mr="xl" mt="xs">
      <Stack>
        <Text weight="bold">Visible events</Text>
        <Stack spacing="sm">
          {filters.reporter ? (
            <ReporterStatusFilter
              values={filters.reporter_status_in}
              onChange={statuses =>
                setFilters(filters => ({
                  ...filters,
                  reporter_status_in: statuses,
                }))
              }
            />
          ) : (
            <GeneralStatusFilter
              values={filters.general_status_in}
              onChange={statuses =>
                setFilters(filters => ({
                  ...filters,
                  general_status_in: statuses,
                }))
              }
            />
          )}
        </Stack>
        <Text weight="bold" mt="sm">
          Filters
        </Text>
        <Stack spacing="md">
          <SearchFilter
            value={filters.event_search ?? ''}
            placeholder="Search name"
            onChange={event_search =>
              setFilters(filters => ({ ...filters, event_search }))
            }
          />
          <SearchFilter
            value={filters.team_search ?? ''}
            placeholder="Search team"
            onChange={team_search =>
              setFilters(filters => ({ ...filters, team_search }))
            }
          />
          <SearchFilter
            placeholder="Search organization"
            value={filters.organization_search ?? ''}
            onChange={organization_search =>
              setFilters(filters => ({ ...filters, organization_search }))
            }
          />
          <ReporterFilter
            reporters={reporters}
            value={filters.reporter}
            onChange={reporter =>
              setFilters(filters => ({
                ...filters,
                reporter,
                event_level_in: undefined,
              }))
            }
          />
          <EventLevelFilter
            levels={LEVELS}
            placeholder="All levels"
            value={filters.event_level_in}
            maxLevel={maxLevel}
            onChange={event_level_in =>
              setFilters(filters => ({ ...filters, event_level_in }))
            }
          />
          <StateFilter
            value={filters?.state_in}
            onChange={state_in => setFilters({ ...filters, state_in })}
          />
          <BookedByReporterFromDifferentStateFilter
            value={filters?.booked_by_reporter_from_different_state}
            onChange={booked_by_reporter_from_different_state =>
              setFilters({
                ...filters,
                booked_by_reporter_from_different_state,
              })
            }
          />
        </Stack>
      </Stack>
    </Box>
  )
}
